import { Elements } from "@stripe/react-stripe-js";
import type { Appearance, StripeElementsOptions } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { useServiceFactory } from "../../../app/appServices";
import { Blocked } from "../../../components/blocked/blocked";
import { Loader } from "../../../components/loader/loader";
import { Status } from "../../../components/status/status";
import { ErrorState } from "../../../helpers/error";
import { useMount } from "../../../helpers/reactHooks";
import type { PaymentInfo } from "../../../types/payment";
import { CheckoutForm } from "./checkoutForm";
import styles from "./CardPage.module.scss";

type CardPageProps = {
  details: PaymentInfo;
};

export const CardPage: FC<CardPageProps> = observer(({ details }) => {
  const store = useServiceFactory((x) => x.createCardPageStore());
  const { clientSecret } = details.stripePaymentIntent;
  const appearance: Appearance = {
    theme: "stripe",
  };
  const options: StripeElementsOptions = {
    clientSecret,
    appearance,
  };

  useMount(async () => {
    await store.load();
  });

  if (!clientSecret || store.state instanceof ErrorState) {
    return (
      <>
        <Status type="error" backUrl={details.backUrl} title="Page load error..." text="We are already working on it" />
        <Blocked
          name={details.toAccountName}
          accountNumber={details.openbankingPaymentRequest.toAccountNumber}
          sortCode={details.openbankingPaymentRequest.toAccountSortCode}
        />
      </>
    );
  }

  if (store.state === "loading") {
    return <Loader className={styles.loader} />;
  }

  return (
    <div className={styles.panelCard}>
      <div className={styles.content}>
        <div className={styles.title}>Enter your card details</div>
        <div className={styles.cardForm}>
          <Elements
            options={options}
            stripe={loadStripe(store.state.apiKey, {
              locale: "en-GB",
              stripeAccount: details.stripePaymentIntent.accountId,
            })}
          >
            <CheckoutForm />
          </Elements>
        </div>
      </div>
    </div>
  );
});
